import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Users, Mail, TrendingUp, BarChart3, Eye, Clock, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, contactsRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/newsletter`)
      ]);

      setStats(statsRes.data);
      setContacts(contactsRes.data);
      setSubscribers(subscribersRes.data);
      setLoading(false);
    } catch (error) {
      // Error fetching dashboard data - handle gracefully
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-32 pb-20 flex items-center justify-center">
        <div className="text-white text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 flex justify-between items-start">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Admin <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">Dashboard</span>
            </h1>
            <p className="text-gray-400 text-lg">Manage contacts, subscribers, and monitor website activity</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right mr-4">
              <p className="text-sm text-gray-500">Logged in as</p>
              <p className="text-white font-medium">{user?.email}</p>
            </div>
            <Button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-300"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Card className="bg-gradient-to-br from-cyan-900/40 to-black border border-cyan-500/30 p-6 rounded-2xl">
            <div className="flex items-center justify-between mb-4">
              <Users className="text-cyan-400" size={32} />
              <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">Total</Badge>
            </div>
            <h3 className="text-3xl font-bold text-white mb-2">{stats?.contacts?.total || 0}</h3>
            <p className="text-gray-400">Total Contacts</p>
            <p className="text-cyan-400 text-sm mt-2">{stats?.contacts?.new || 0} new</p>
          </Card>

          <Card className="bg-gradient-to-br from-purple-900/40 to-black border border-purple-500/30 p-6 rounded-2xl">
            <div className="flex items-center justify-between mb-4">
              <Mail className="text-purple-400" size={32} />
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">Active</Badge>
            </div>
            <h3 className="text-3xl font-bold text-white mb-2">{stats?.newsletter?.active_subscribers || 0}</h3>
            <p className="text-gray-400">Newsletter Subscribers</p>
          </Card>

          <Card className="bg-gradient-to-br from-green-900/40 to-black border border-green-500/30 p-6 rounded-2xl">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="text-green-400" size={32} />
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Growth</Badge>
            </div>
            <h3 className="text-3xl font-bold text-white mb-2">{stats?.contacts?.contacted || 0}</h3>
            <p className="text-gray-400">Contacted</p>
          </Card>

          <Card className="bg-gradient-to-br from-orange-900/40 to-black border border-orange-500/30 p-6 rounded-2xl">
            <div className="flex items-center justify-between mb-4">
              <BarChart3 className="text-orange-400" size={32} />
              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Closed</Badge>
            </div>
            <h3 className="text-3xl font-bold text-white mb-2">{stats?.contacts?.closed || 0}</h3>
            <p className="text-gray-400">Closed Deals</p>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-gray-800">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'overview'
                ? 'text-cyan-400 border-b-2 border-cyan-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'contacts'
                ? 'text-cyan-400 border-b-2 border-cyan-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Contacts ({contacts.length})
          </button>
          <button
            onClick={() => setActiveTab('subscribers')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'subscribers'
                ? 'text-cyan-400 border-b-2 border-cyan-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Subscribers ({subscribers.length})
          </button>
        </div>

        {/* Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Contacts */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 p-6 rounded-3xl">
              <h3 className="text-2xl font-bold text-white mb-6">Recent Contacts</h3>
              <div className="space-y-4">
                {contacts.slice(0, 5).map((contact) => (
                  <div key={contact.id} className="flex items-start justify-between p-4 bg-gray-900/50 rounded-xl border border-gray-800 hover:border-cyan-500/30 transition-colors">
                    <div className="flex-1">
                      <h4 className="text-white font-semibold">{contact.name}</h4>
                      <p className="text-gray-400 text-sm">{contact.email}</p>
                      <p className="text-gray-500 text-xs mt-1">{formatDate(contact.created_at)}</p>
                    </div>
                    <Badge className={`${
                      contact.status === 'new' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                      contact.status === 'contacted' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                      'bg-gray-500/20 text-gray-400 border-gray-500/30'
                    }`}>
                      {contact.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>

            {/* Recent Subscribers */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 p-6 rounded-3xl">
              <h3 className="text-2xl font-bold text-white mb-6">Recent Subscribers</h3>
              <div className="space-y-4">
                {subscribers.slice(0, 5).map((subscriber) => (
                  <div key={subscriber.id} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-xl border border-gray-800 hover:border-purple-500/30 transition-colors">
                    <div>
                      <p className="text-white font-semibold">{subscriber.email}</p>
                      <p className="text-gray-500 text-xs mt-1">{formatDate(subscriber.subscribed_at)}</p>
                    </div>
                    <Mail className="text-purple-400" size={20} />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'contacts' && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 p-6 rounded-3xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left text-gray-400 font-semibold py-4 px-4">Name</th>
                    <th className="text-left text-gray-400 font-semibold py-4 px-4">Email</th>
                    <th className="text-left text-gray-400 font-semibold py-4 px-4">Phone</th>
                    <th className="text-left text-gray-400 font-semibold py-4 px-4">Message</th>
                    <th className="text-left text-gray-400 font-semibold py-4 px-4">Date</th>
                    <th className="text-left text-gray-400 font-semibold py-4 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr key={contact.id} className="border-b border-gray-800 hover:bg-gray-900/50">
                      <td className="py-4 px-4 text-white">{contact.name}</td>
                      <td className="py-4 px-4 text-gray-400">{contact.email}</td>
                      <td className="py-4 px-4 text-gray-400">{contact.phone}</td>
                      <td className="py-4 px-4 text-gray-400 max-w-xs truncate">{contact.message}</td>
                      <td className="py-4 px-4 text-gray-400 text-sm">{formatDate(contact.created_at)}</td>
                      <td className="py-4 px-4">
                        <Badge className={`${
                          contact.status === 'new' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                          contact.status === 'contacted' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                          'bg-gray-500/20 text-gray-400 border-gray-500/30'
                        }`}>
                          {contact.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {activeTab === 'subscribers' && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 p-6 rounded-3xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left text-gray-400 font-semibold py-4 px-4">Email</th>
                    <th className="text-left text-gray-400 font-semibold py-4 px-4">Subscribed Date</th>
                    <th className="text-left text-gray-400 font-semibold py-4 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="border-b border-gray-800 hover:bg-gray-900/50">
                      <td className="py-4 px-4 text-white">{subscriber.email}</td>
                      <td className="py-4 px-4 text-gray-400">{formatDate(subscriber.subscribed_at)}</td>
                      <td className="py-4 px-4">
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          {subscriber.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
